import { useQuery } from "@tanstack/react-query";
import type { QueryObserverResult } from "@tanstack/react-query";
import { revenueApi } from "@/lib/api";
import type { RevenueStatement } from "@/types";
import { POLLING_INTERVAL } from "@/hooks/useInterval";
import { isStatementBusy } from "@/lib/revenue";

interface UseRevenueStatementOptions {
  statementId: string | undefined;
  enabled?: boolean;
}

interface UseRevenueStatementReturn {
  statement: RevenueStatement | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => Promise<QueryObserverResult<RevenueStatement, Error>>;
}

export const useRevenueStatement = ({
  statementId,
  enabled = true,
}: UseRevenueStatementOptions): UseRevenueStatementReturn => {
  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    queryKey: ["revenue-statement", statementId],
    queryFn: async (): Promise<RevenueStatement> => {
      if (!statementId) {
        throw new Error("Revenue statement ID is required");
      }
      return await revenueApi.getRevenueStatement(statementId);
    },
    enabled: enabled && !!statementId,
    // Amount received depends on ledger processing that finishes after the
    // import batch reports complete, and settlement books in a background
    // task — keep polling until the statement reaches a terminal status.
    refetchInterval: (query) =>
      query.state.data && isStatementBusy(query.state.data)
        ? POLLING_INTERVAL
        : false,
  });

  return {
    statement: data,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
